import type {
  FetchProcessDetailsResponseData,
  FetchProcessesResponseData,
} from 'gcs-common/clients/api/entities/processes/processes-api-helpers';
import {
  getSelectedProduct,
  useGetProcessProductsList,
} from 'gcs-common/clients/api/entities/processes/processes-api-selectors';
import Alert from 'gcs-common/components/Alert/Alert';
import LoadingIndicator from 'gcs-common/components/LoadingIndicator/LoadingIndicator';
import ProductListItem from 'gcs-common/components/ProcessDetail/ProductListItem/ProductListItem';
import {
  convertOrderStatusToString,
  convertProcessTypeToString,
  getUniqueProcessNumber,
  getUniqueProductNumber,
} from 'gcs-common/helper/processTextHelper';
import { getChannelOPUserId, getPrimaryHouseForChannel } from 'gcs-common/slices/channel/channelSelectors';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import { getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { channelInputCleared } from 'gcs-common/slices/messageInput/messageInputSlice';
import { processSelected, productSelected } from 'gcs-common/slices/processes/processesSlice';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import backIcon from '../../img/back.svg';
import ProductDetail from './ProductDetail/ProductDetail';
import RenderButtons from './RenderButtons/RenderButtons';
import styles from './styles.module.scss';

interface ProcessDetailProps {
  process: FetchProcessesResponseData[number];
}

const ProcessDetail = (props: ProcessDetailProps) => {
  const { process: selectedProcess } = props;

  const {
    ProcessNumber,
    ProcessSuffix: Suffix,
    OrderStatusText,
    OrderStatus,
    CreationDate: CreateDate,
    CustomerOrderText,
    CustomerOrderNumber,
    ProcessType,
    ProcessTypeDescription,
  } = selectedProcess;

  const selectedChannelId: string = useSelector(getSelectedChannelId);
  const selectedProduct = getSelectedProduct();
  const channelOpUserId: string = useSelector(getChannelOPUserId(selectedChannelId));
  const houseId: string = useSelector(getPrimaryHouseForChannel(selectedChannelId));
  const isAgent: boolean = useSelector(getIsAgent);

  const dispatch = useDispatch();

  const {
    data: products,
    isFetching,
    error,
  } = useGetProcessProductsList({
    house_id: houseId,
    process_number: ProcessNumber,
    suffix: Suffix,
    user_id: isAgent ? channelOpUserId : undefined,
  });

  const List = products || [];

  const requestClose = () => {
    if (selectedProduct) {
      dispatch(productSelected({ selectedProductNumber: undefined }));
    } else {
      dispatch(processSelected({ selectedProcessNumber: undefined }));
      dispatch(channelInputCleared({ channelId: selectedChannelId }));
    }
  };

  const handleSelectProduct = (product: FetchProcessDetailsResponseData['items'][number]) => {
    dispatch(
      productSelected({
        selectedProductNumber: getUniqueProductNumber(product.ArticleNumber, product.Id),
      }),
    );
  };

  return (
    <div className={styles.selectedProcessWrapper}>
      <div className={styles.selectedProcessHeader}>
        <button type="button" onClick={requestClose}>
          <img className={styles.backButtonIcon} src={backIcon} alt="exit-icon" />
        </button>
      </div>
      {selectedProduct ? (
        <ProductDetail product={selectedProduct} />
      ) : (
        <>
          <div className={styles.selectedProcessInfo}>
            <div className={styles.dateTag}>{moment(CreateDate).format('dddd,  L')}</div>
            <div className={styles.processName}>
              {`${convertProcessTypeToString(ProcessType, ProcessTypeDescription)}: `}
              <span>{getUniqueProcessNumber(ProcessNumber, Suffix)}</span>
            </div>
            <div className={styles.processHint}>Auftragsnummer: {CustomerOrderNumber}</div>
            <div className={styles.processHint}>Auftragstext: {CustomerOrderText}</div>
            <div
              className={styles.processState}
            >{`Status: ${convertOrderStatusToString(OrderStatus, OrderStatusText, ProcessType) || 'Nicht verfügbar'}`}</div>
          </div>
          {error ? (
            <div className={styles.errorMessage}>
              <Alert type="info">
                <p className={styles.errorMessageText}>{error.message}</p>
              </Alert>
            </div>
          ) : (
            <div className={styles.selectedProcessProductList}>
              {List?.map((product) => (
                <ProductListItem
                  key={getUniqueProductNumber(product.ArticleNumber, product.Id)}
                  product={product}
                  onSelectProduct={() => handleSelectProduct(product)}
                />
              ))}
            </div>
          )}
        </>
      )}
      <div className={styles.buttonContainer}>
        {isFetching && <LoadingIndicator />}
        {!isFetching && <RenderButtons process={selectedProcess} product={selectedProduct} />}
      </div>
    </div>
  );
};

export default ProcessDetail;
